import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import { Canvg } from 'canvg';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ReactComponent as PdfIcon } from '../Assets/pdf_icon.svg';

const SaveConfirmModal = ({ isOpen, onClose, totalCost, svgRef, onSendEnquiry }) => {
    const { t } = useTranslation();
    const [isSaving, setIsSaving] = useState(false);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('uk-UA', { style: 'currency', currency: 'UAH', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price);
    };

    const handleSavePdf = async () => {
        if (!svgRef.current) {
            toast.error(t('saveConfirmModal.toasts.svgError'));
            return;
        }
        setIsSaving(true);
        try {
            const svgElement = svgRef.current;
            const svgString = new XMLSerializer().serializeToString(svgElement);
            
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            const { width, height } = svgElement.getBoundingClientRect();
            // Додаємо невеликий множник для кращої якості
            const scaleFactor = 2;
            canvas.width = width * scaleFactor;
            canvas.height = height * scaleFactor;
            ctx.scale(scaleFactor, scaleFactor);

            const v = await Canvg.from(ctx, svgString, {
                ignoreMouse: true,
                ignoreAnimation: true,
                ignoreDimensions: true,
            });
            await v.render();

            const imgData = canvas.toDataURL('image/png');
            
            const pdf = new jsPDF({
                orientation: width > height ? 'landscape' : 'portrait',
                unit: 'px',
                format: [width, height]
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            pdf.setFontSize(16);
            pdf.text(t('saveConfirmModal.title'), pdfWidth / 2, 20, { align: 'center' });
            
            pdf.setFontSize(12);
            pdf.text(`${t('saveConfirmModal.estimatedCost')}: ${formatPrice(totalCost)}`, pdfWidth / 2, 40, { align: 'center' });
            
            pdf.addImage(imgData, 'PNG', 0, 60, pdfWidth, pdfHeight - 70);

            pdf.save("RebBud_Project.pdf");
            toast.success(t('saveConfirmModal.toasts.saveSuccess'));
            onClose();

        } catch (error) {
            console.error("PDF Generation Error:", error);
            toast.error(t('saveConfirmModal.toasts.saveError'));
        } finally {
            setIsSaving(false);
        }
    };
    
    const modalVariants = {
        hidden: { opacity: 0, scale: 0.95, y: 20 },
        visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } },
        exit: { opacity: 0, scale: 0.95, y: 20, transition: { duration: 0.2, ease: "easeIn" } },
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <motion.div
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="bg-white rounded-lg shadow-xl w-full max-w-sm border border-gray-200/80"
                    >
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-md font-semibold text-gray-800 tracking-wide">{t('saveConfirmModal.title')}</h3>
                            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full">
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 text-center">
                            <p className="text-sm text-gray-600 mb-1">{t('saveConfirmModal.estimatedCost')}</p>
                            <p className="text-3xl font-bold text-gray-800 mb-6">{formatPrice(totalCost)}</p>
                            
                            <button
                                onClick={handleSavePdf}
                                disabled={isSaving}
                                className="w-full flex items-center justify-center px-4 py-2.5 mb-4 bg-white border border-gray-300 text-gray-700 font-medium text-sm rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                <PdfIcon className="w-5 h-5 mr-2" />
                                {isSaving ? t('saveConfirmModal.savingButton') : t('saveConfirmModal.downloadAsPdf')}
                            </button>
                        </div>
                        
                        <div className="px-6 py-4 bg-gray-50/70 rounded-b-lg flex items-center justify-between space-x-3">
                           <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2.5 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                                {t('saveConfirmModal.backButton')}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    onClose();
                                    onSendEnquiry();
                                }}
                                className="flex-1 flex items-center justify-center px-5 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-semibold text-white bg-green-600 hover:bg-green-700"
                            >
                                {t('saveConfirmModal.orderButton')}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default SaveConfirmModal;